/**
 * Email store module for managing inbox functionality
 */
import { defineStore } from 'pinia'
import { adminApi } from '@/services/api'

// Email folders configuration
const DEFAULT_FOLDERS = [
  { id: 'inbox', name: 'Inbox', icon: 'mdi-inbox', unreadCount: 0 },
  { id: 'support', name: 'Support', icon: 'mdi-help-circle', unreadCount: 0 },
  { id: 'sales', name: 'Sales', icon: 'mdi-cash', unreadCount: 0 },
  { id: 'sent', name: 'Sent', icon: 'mdi-send', unreadCount: 0 },
  { id: 'starred', name: 'Starred', icon: 'mdi-star', unreadCount: 0 },
  { id: 'archive', name: 'Archive', icon: 'mdi-archive', unreadCount: 0 },
  { id: 'all', name: 'All Mail', icon: 'mdi-email', unreadCount: 0 }
]

export const useEmailStore = defineStore('email', {
  state: () => ({
    emails: [],
    folders: DEFAULT_FOLDERS,
    loading: false,
    error: null,
    lastSync: null
  }),

  getters: {
    unreadCount: (state) => {
      return state.emails.filter(e => !e.read && !e.archived).length
    },
    
    supportEmails: (state) => {
      return state.emails.filter(e => e.folder === 'support')
    },
    
    salesEmails: (state) => {
      return state.emails.filter(e => e.folder === 'sales')
    },
    
    starredEmails: (state) => {
      return state.emails.filter(e => e.starred)
    }
  },

  actions: {
    async fetchEmails() {
      this.loading = true
      this.error = null
      
      try {
        const response = await adminApi.getEmails()
        this.emails = response.data.emails || []
        this.lastSync = new Date()
        
        // Update folder counts
        this.updateFolderCounts()
        
        return this.emails
      } catch (error) {
        this.error = error.message
        console.error('Failed to fetch emails:', error)
        
        // Use mock data for development
        if (import.meta.env.DEV) {
          this.emails = this.getMockEmails()
          this.updateFolderCounts()
        }
      } finally {
        this.loading = false
      }
    },

    async markAsRead(emailId) {
      const email = this.emails.find(e => e.id === emailId)
      if (email && !email.read) {
        email.read = true
        this.updateFolderCounts()
        
        try {
          await adminApi.markEmailRead(emailId)
        } catch (error) {
          console.error('Failed to mark email as read:', error)
          email.read = false
        }
      }
    },

    async toggleStar(emailId) {
      const email = this.emails.find(e => e.id === emailId)
      if (email) {
        email.starred = !email.starred
        
        try {
          await adminApi.toggleEmailStar(emailId, email.starred)
        } catch (error) {
          console.error('Failed to toggle star:', error)
          email.starred = !email.starred
        }
      }
    },

    async archiveEmail(emailId) {
      const email = this.emails.find(e => e.id === emailId)
      if (email) {
        email.archived = true
        email.folder = 'archive'
        this.updateFolderCounts()
        
        try {
          await adminApi.archiveEmail(emailId)
        } catch (error) {
          console.error('Failed to archive email:', error)
          email.archived = false
        }
      }
    },

    async deleteEmail(emailId) {
      const index = this.emails.findIndex(e => e.id === emailId)
      if (index !== -1) {
        const email = this.emails[index]
        this.emails.splice(index, 1)
        this.updateFolderCounts()
        
        try {
          await adminApi.deleteEmail(emailId)
        } catch (error) {
          console.error('Failed to delete email:', error)
          this.emails.splice(index, 0, email)
          this.updateFolderCounts()
        }
      }
    },

    updateFolderCounts() {
      this.folders.forEach(folder => {
        switch (folder.id) {
          case 'inbox':
            folder.unreadCount = this.emails.filter(e => 
              !e.read && !e.archived && e.folder === 'inbox'
            ).length
            break
          case 'support':
            folder.unreadCount = this.emails.filter(e => 
              !e.read && e.folder === 'support'
            ).length
            break
          case 'sales':
            folder.unreadCount = this.emails.filter(e => 
              !e.read && e.folder === 'sales'
            ).length
            break
          case 'starred':
            folder.unreadCount = this.emails.filter(e => 
              !e.read && e.starred
            ).length
            break
          case 'all':
            folder.unreadCount = this.emails.filter(e => !e.read).length
            break
          default:
            folder.unreadCount = 0
        }
      })
    },

    getMockEmails() {
      return [
        {
          id: '1',
          from: 'john.doe@example.com',
          to: 'support@aushadhhealth.com',
          subject: 'Issue with medication reminder',
          preview: 'Hi, I\'m having trouble setting up medication reminders...',
          body: '<p>Hi Support Team,</p><p>I\'m having trouble setting up medication reminders. The app crashes when I try to add a new medication.</p><p>Please help!</p><p>Thanks,<br>John</p>',
          folder: 'support',
          read: false,
          starred: false,
          archived: false,
          receivedAt: new Date().toISOString()
        },
        {
          id: '2',
          from: 'jane.smith@hospital.com',
          to: 'sales@aushadhhealth.com',
          subject: 'Enterprise pricing inquiry',
          preview: 'We are interested in deploying Medizen across our hospital network...',
          body: '<p>Dear Sales Team,</p><p>We are interested in deploying Medizen across our hospital network of 15 locations.</p><p>Could you please provide enterprise pricing information?</p><p>Best regards,<br>Jane Smith<br>IT Director</p>',
          folder: 'sales',
          read: false,
          starred: true,
          archived: false,
          receivedAt: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: '3',
          from: 'support@cloudflare.com',
          to: 'admin@aushadhhealth.com',
          subject: 'Your domain is now active',
          preview: 'Great news! Your domain aushadhhealth.com is now active on Cloudflare...',
          body: '<p>Your domain configuration has been completed successfully.</p>',
          folder: 'inbox',
          read: true,
          starred: false,
          archived: false,
          receivedAt: new Date(Date.now() - 86400000).toISOString()
        }
      ]
    }
  }
})